import { useState } from "react";
import { toast } from "sonner";
import { MapPin, Pencil, Plus, Trash2 } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/auth";
import { errorText } from "@/lib/errors";
import { formatRange } from "@/components/site/Retreats";
import { RetreatForm } from "./RetreatForm";
import { Empty, Loading, useAdminList } from "./shared";

const DeleteRetreat = ({ r, onDone }) => {
  const remove = async () => {
    try {
      await adminApi.delete(`/admin/retreats/${r.id}`);
      toast.success("Retreat deleted.");
      onDone();
    } catch (err) {
      toast.error(errorText(err));
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <button type="button" className="inline-flex items-center gap-1 text-sm text-moss hover:text-red-800" data-testid={`retreat-delete-${r.id}`}><Trash2 size={14} /> Delete</button>
      </AlertDialogTrigger>
      <AlertDialogContent className="site bg-paper">
        <AlertDialogHeader>
          <AlertDialogTitle className="dp text-2xl font-normal">Delete "{r.title}"?</AlertDialogTitle>
          <AlertDialogDescription>It disappears from the website. Existing reservations stay in your inbox.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="retreat-delete-dismiss">Keep it</AlertDialogCancel>
          <AlertDialogAction onClick={remove} className="bg-pine" data-testid="retreat-delete-confirm">Delete</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export const RetreatsTab = () => {
  const { items, reload } = useAdminList("/admin/retreats");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const openForm = (r) => { setEditing(r); setOpen(true); };

  return (
    <div>
      <button type="button" onClick={() => openForm(null)} className="mb-6 inline-flex items-center gap-2 rounded-full bg-pine text-sage px-6 py-3" data-testid="retreat-add-button">
        <Plus size={16} /> Add a retreat
      </button>
      {!items && <Loading />}
      {items && !items.length && <Empty testId="admin-retreats-empty">No retreats yet. Add one and it will appear on your website.</Empty>}
      <ul className="grid gap-4" data-testid="admin-retreats-list">
        {items?.map((r) => (
          <li key={r.id} className="bg-paper rounded-2xl p-6 grid sm:grid-cols-[1fr_auto] gap-4" data-testid={`admin-retreat-${r.id}`}>
            <div>
              <p className="lb text-brass-ink">{formatRange(r.start_date, r.end_date)} · {r.status === "open" ? "Open" : "Hidden"}</p>
              <p className="dp text-2xl mt-2">{r.title}</p>
              <p className="text-sm text-moss flex items-center gap-1"><MapPin size={14} /> {r.location}</p>
              <p className="mt-2 text-sm tn">{r.seats_reserved} of {r.seats_total} seats reserved</p>
            </div>
            <div className="flex sm:flex-col gap-4 items-start">
              <button type="button" onClick={() => openForm(r)} className="inline-flex items-center gap-1 text-sm hover:text-brass-ink" data-testid={`retreat-edit-${r.id}`}><Pencil size={14} /> Edit</button>
              <DeleteRetreat r={r} onDone={reload} />
            </div>
          </li>
        ))}
      </ul>
      <RetreatForm open={open} onOpenChange={setOpen} retreat={editing} onSaved={reload} />
    </div>
  );
};
